import axios from 'axios';

const API_BASE_URL = 'https://examevoke.in/api/admin';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password,
      });
      
      if (response.data.success) {
        const { token, admin } = response.data.data;
        localStorage.setItem('authToken', token);
        localStorage.setItem('adminUser', JSON.stringify(admin));
        return { success: true, data: response.data.data };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed. Please check your credentials.' 
      };
    }
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminUser');
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('adminUser');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },
};

// Dashboard APIs
export const dashboardAPI = {
  // 1. Student Overview
  getOverview: async () => {
    try {
      const response = await apiClient.get('/dashboard/overview');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching overview:', error);
      return { success: false, error: error.message };
    }
  },

  // 2. Year-wise Distribution
  getYearDistribution: async () => {
    try {
      const response = await apiClient.get('/dashboard/year-distribution');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching year distribution:', error);
      return { success: false, error: error.message };
    }
  },

  // 3. Domain Enrollments
  getDomainEnrollments: async () => {
    try {
      const response = await apiClient.get('/dashboard/domain-enrollments');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching domain enrollments:', error);
      return { success: false, error: error.message };
    }
  },

  // 4. Multi-domain Stats
  getMultiDomainStats: async () => {
    try {
      const response = await apiClient.get('/dashboard/multi-domain-stats');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching multi-domain stats:', error);
      return { success: false, error: error.message };
    }
  },

  // 5. Weekly Progress
  getWeeklyProgress: async () => {
    try {
      const response = await apiClient.get('/dashboard/weekly-progress');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching weekly progress:', error);
      return { success: false, error: error.message };
    }
  },

  // 6. Task Trends
  getTaskTrends: async () => {
    try {
      const response = await apiClient.get('/dashboard/task-trends');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching task trends:', error);
      return { success: false, error: error.message };
    }
  },

  // 7. Domain Progress
  getDomainProgress: async () => {
    try {
      const response = await apiClient.get('/dashboard/domain-progress');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching domain progress:', error);
      return { success: false, error: error.message };
    }
  },

  // Fetch all dashboard data at once
  getAllDashboardData: async () => {
    try {
      const [
        overview,
        // yearDistribution,
        // domainEnrollments,
        // multiDomainStats,
        weeklyProgress,
        taskTrends,
        // domainProgress,
      ] = await Promise.all([
        dashboardAPI.getOverview(),
        // dashboardAPI.getYearDistribution(),
        // dashboardAPI.getDomainEnrollments(),
        // dashboardAPI.getMultiDomainStats(),
        dashboardAPI.getWeeklyProgress(),
        dashboardAPI.getTaskTrends(),
        // dashboardAPI.getDomainProgress(),
      ]);

      return {
        success: true,
        data: {
          overview: overview.data,
          // yearDistribution: yearDistribution.data,
          // domainEnrollments: domainEnrollments.data,
          // multiDomainStats: multiDomainStats.data,
          weeklyProgress: weeklyProgress.data,
          taskTrends: taskTrends.data,
          // domainProgress: domainProgress.data,
        },
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return { success: false, error: error.message };
    }
  },

  // 8. Student Progress Filters
  getStudentFilters: async () => {
    try {
      const response = await apiClient.get('/dashboard/students/filters');
      return { success: true, data: response.data.filters };
    } catch (error) {
      console.error('Error fetching student filters:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  // 9. Student Progress with Filters and Pagination
  getStudentProgress: async (params = {}) => {
    try {
      const {
        page = 1,
        per_page = 10,
        year = null,
        branch = null,
        domain = null,
        activity = null,
        search = null,
        sort_by = 'name',
        sort_order = 'asc',
      } = params;

      // Build query string
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('per_page', per_page.toString());
      
      if (year) queryParams.append('year', year);
      if (branch) queryParams.append('branch', branch);
      if (domain) queryParams.append('domain', domain);
      if (activity) queryParams.append('activity', activity);
      if (search) queryParams.append('search', search);
      if (sort_by) queryParams.append('sort_by', sort_by);
      if (sort_order) queryParams.append('sort_order', sort_order);

      const response = await apiClient.get(
        `/dashboard/students/progress?${queryParams.toString()}`
      );
      
      return { 
        success: true, 
        data: response.data.data,
        pagination: response.data.pagination,
        filters: response.data.filters,
        sort: response.data.sort,
      };
    } catch (error) {
      console.error('Error fetching student progress:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  // 10. Export Student Progress Data
  exportStudentProgress: async (params = {}) => {
    try {
      const {
        year = null,
        branch = null,
        domain = null,
        activity = null,
        search = null,
        sort_by = 'name',
        sort_order = 'asc',
      } = params;

      // Build query string
      const queryParams = new URLSearchParams();
      
      if (year) queryParams.append('year', year);
      if (branch) queryParams.append('branch', branch);
      if (domain) queryParams.append('domain', domain);
      if (activity) queryParams.append('activity', activity);
      if (search) queryParams.append('search', search);
      if (sort_by) queryParams.append('sort_by', sort_by);
      if (sort_order) queryParams.append('sort_order', sort_order);

      const response = await apiClient.get(
        `/dashboard/students/progress/export?${queryParams.toString()}`,
        {
          responseType: 'blob', // Important for file download
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student_progress_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error exporting student progress:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  // 11. AI Interview Overview
  getInterviewOverview: async () => {
    try {
      const response = await apiClient.get('/dashboard/interviews/overview');
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching interview overview:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  // 12. AI Interview Performance Distribution
  getInterviewPerformanceDistribution: async (startDate, endDate) => {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);

      const response = await apiClient.get(
        `/dashboard/interviews/performance-distribution?${queryParams.toString()}`
      );
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching interview performance distribution:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  // 13. AI Interview Students List
  getInterviewStudents: async (params = {}) => {
    try {
      const {
        page = 1,
        per_page = 50,
        search = null,
        sort_by = 'name',
        sort_order = 'asc',
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('per_page', per_page.toString());
      
      if (search) queryParams.append('search', search);
      if (sort_by) queryParams.append('sort_by', sort_by);
      if (sort_order) queryParams.append('sort_order', sort_order);

      const response = await apiClient.get(
        `/dashboard/interviews/students?${queryParams.toString()}`
      );
      
      return { 
        success: true, 
        data: response.data.data,
        pagination: response.data.pagination,
      };
    } catch (error) {
      console.error('Error fetching interview students:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },

  // 14. AI Interview Student Details
  getInterviewStudentDetails: async (email) => {
    try {
      const response = await apiClient.get(
        `/dashboard/interviews/student/${encodeURIComponent(email)}`
      );
      return { success: true, data: response.data.data };
    } catch (error) {
      console.error('Error fetching interview student details:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message 
      };
    }
  },
};

export default apiClient;
